import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CaseIdSchema, PeriodSchema, type Period } from '@casetrace/shared';
import { collection, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/auth-context';
import { db } from '../lib/firebase';
import { periodsQueryKey } from './use-case-periods';

type PeriodListContext = { previous: Period[] | undefined };

export type CreatePeriodInput = {
  readonly label: string;
  readonly description?: string;
  readonly from: string;
  readonly to: string;
  readonly color: string;
};

export type UpdatePeriodInput = {
  readonly periodId: string;
  readonly label: string;
  readonly description?: string;
  readonly from: string;
  readonly to: string;
  readonly color: string;
};

function readPeriodList(queryClient: QueryClient, caseId: string): Period[] {
  return queryClient.getQueryData<Period[]>(periodsQueryKey(caseId)) ?? [];
}

function writePeriodList(queryClient: QueryClient, caseId: string, next: Period[]): void {
  queryClient.setQueryData(periodsQueryKey(caseId), next);
}

function buildCreatePayload(
  caseId: string,
  uid: string,
  periodId: string,
  input: CreatePeriodInput,
): Period {
  return PeriodSchema.parse({
    id: periodId,
    caseId: CaseIdSchema.parse(caseId),
    label: input.label.trim(),
    description: input.description?.trim() || undefined,
    from: input.from,
    to: input.to,
    color: input.color,
    createdBy: uid,
    createdAt: new Date().toISOString(),
  });
}

function buildUpdatePayload(existing: Period, input: UpdatePeriodInput): Period {
  return PeriodSchema.parse({
    ...existing,
    label: input.label.trim(),
    description: input.description?.trim() || undefined,
    from: input.from,
    to: input.to,
    color: input.color,
  });
}

function sortPeriods(items: Period[]): Period[] {
  return items.slice().sort((a, b) => a.from.localeCompare(b.from) || a.id.localeCompare(b.id));
}

export function useCreatePeriod(caseId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = periodsQueryKey(caseId);

  return useMutation({
    mutationFn: async (input: CreatePeriodInput) => {
      const uid = user?.uid;
      if (!uid) throw new Error('Du må være innlogget for å opprette tidsslotter.');
      const parsedCaseId = CaseIdSchema.parse(caseId);
      const ref = doc(collection(db, 'cases', parsedCaseId, 'periods'));
      const payload = buildCreatePayload(caseId, uid, ref.id, input);
      await setDoc(ref, payload);
      return payload;
    },
    onMutate: async (input: CreatePeriodInput): Promise<PeriodListContext> => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Period[]>(queryKey);
      const uid = user?.uid;
      if (!uid) return { previous };

      const optimisticId = `optimistic-${Date.now()}`;
      const optimistic = buildCreatePayload(caseId, uid, optimisticId, input);
      const next = sortPeriods([...(previous ?? []), optimistic]);
      writePeriodList(queryClient, caseId, next);
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}

export function useUpdatePeriod(caseId: string) {
  const queryClient = useQueryClient();
  const queryKey = periodsQueryKey(caseId);

  return useMutation({
    mutationFn: async (input: UpdatePeriodInput) => {
      const parsedCaseId = CaseIdSchema.parse(caseId);
      const current = readPeriodList(queryClient, caseId);
      const existing = current.find((item) => item.id === input.periodId);
      if (!existing) throw new Error('Fant ikke tidsslotten.');
      const payload = buildUpdatePayload(existing, input);
      const ref = doc(db, 'cases', parsedCaseId, 'periods', payload.id);
      await setDoc(ref, payload);
      return payload;
    },
    onMutate: async (input: UpdatePeriodInput): Promise<PeriodListContext> => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Period[]>(queryKey);
      const list = previous ?? [];
      const existing = list.find((item) => item.id === input.periodId);
      if (!existing) return { previous };

      const updated = buildUpdatePayload(existing, input);
      const next = sortPeriods(list.map((item) => (item.id === updated.id ? updated : item)));
      writePeriodList(queryClient, caseId, next);
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}

export function useDeletePeriod(caseId: string) {
  const queryClient = useQueryClient();
  const queryKey = periodsQueryKey(caseId);

  return useMutation({
    mutationFn: async (periodId: string) => {
      const parsedCaseId = CaseIdSchema.parse(caseId);
      await deleteDoc(doc(db, 'cases', parsedCaseId, 'periods', periodId));
    },
    onMutate: async (periodId: string): Promise<PeriodListContext> => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Period[]>(queryKey);
      const next = (previous ?? []).filter((item) => item.id !== periodId);
      writePeriodList(queryClient, caseId, next);
      return { previous };
    },
    onError: (_error, _periodId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}
